import React, { useState } from 'react';
import { RiExpandUpDownLine } from 'react-icons/ri';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useSession } from '@/contexts/LitSessionProvider';
import { SESSION_LENGTHS } from '@/hooks/LitSessionContext/useLitSession';
import useMFAUtilities from '@/hooks/LitSessionContext/useMFAUtilities';
import MfaAuthData from '@/components/userSettings/MfAuthData';
import { FaApple, FaEthereum } from 'react-icons/fa';
import MobileMenu from '@/components/shared/MobileMenu';
import MfAuthDesktop from './MfAuthDesktop';
import { FaPhoneVolume, FaWhatsapp } from 'react-icons/fa6';
import { AuthMethodType } from '@lit-protocol/constants';
import { MdOutlineMail } from 'react-icons/md';
import { DesktopOpt } from './DesktopOpt';
import Image from 'next/image';
type SecurityPageProps = {};
type SecurityPageRef = React.ForwardedRef<HTMLDivElement>;
export default function SecurityPage(props: SecurityPageProps, ref: SecurityPageRef) {
  const [loading, setLoading] = useState(false);
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    sessionLength,
    MFAInfo,
    currentAuthMethod,
    setSessionLength
  } = useSession();
  const {
    ToggleMFA
  } = useMFAUtilities({
    loading,
    setLoading,
    error,
    setError
  });
  const checkAuthMethod = (authMethodTypes: number[]): boolean => {
    return authMethodTypes?.includes(currentAuthMethod?.authMethodType);
  };
  const IconClass = 'h-[20px] w-[20px] text-genius-pink';
  const secData = [{
    title: 'Google',
    status: checkAuthMethod([5, 6]) ? 'Synced' : 'Add as an Additional Login Method',
    icon: <Image src='/static/images/google_logo.png' width={16} height={16} alt='google' className='mr-4' />,
    kind: checkAuthMethod([5, 6]) ? '' : 'connect',
    className: checkAuthMethod([5, 6]) ? 'text-green-400' : 'text-genius-cream'
  }, {
    title: 'EVM Wallet',
    status: checkAuthMethod([1]) ? 'Synced' : 'Add as an Additional Login Method',
    icon: <FaEthereum />,
    kind: checkAuthMethod([1]) ? '' : 'connect',
    className: checkAuthMethod([1]) ? 'text-genius-pink' : 'text-genius-cream'
  }, {
    title: 'Apple',
    status: checkAuthMethod([8]) ? 'Coming Soon' : 'Coming Soon',
    icon: <FaApple />,
    kind: '',
    className: checkAuthMethod([8]) ? 'text-genius-pink' : 'text-genius-cream'
  }].filter(item => item.status == 'Synced');
  const mfaAuthData = [{
    title: 'Email',
    authMethodType: AuthMethodType.StytchEmailFactorOtp,
    status: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchEmailFactorOtp ? 'Synced' : 'Add as an Additional Login Method',
    kind: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchEmailFactorOtp ? '' : MFAInfo?.authMethod ? 'wait' : 'connect',
    className: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchEmailFactorOtp ? 'text-genius-pink' : 'text-genius-cream',
    icon: <MdOutlineMail className={IconClass} />
  }, {
    title: 'Text Message',
    authMethodType: AuthMethodType.StytchSmsFactorOtp,
    status: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchSmsFactorOtp ? 'Synced' : 'Add as an Additional Login Method',
    kind: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchSmsFactorOtp ? '' : MFAInfo?.authMethod ? 'wait' : 'connect',
    className: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchSmsFactorOtp ? 'text-genius-pink' : 'text-genius-cream',
    icon: <FaPhoneVolume className={`h-[16.5px] w-[16.5px] text-genius-pink`} />
  }, {
    title: 'WhatsApp',
    authMethodType: AuthMethodType.StytchWhatsAppFactorOtp,
    status: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchWhatsAppFactorOtp ? 'Synced' : 'Add as an Additional Login Method',
    kind: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchWhatsAppFactorOtp ? '' : MFAInfo?.authMethod ? 'wait' : 'connect',
    className: MFAInfo?.authMethod?.authMethodType == AuthMethodType.StytchWhatsAppFactorOtp ? 'text-genius-pink' : 'text-genius-cream',
    icon: <FaWhatsapp className={`h-[21px] w-[21px] text-genius-pink`} />
  }];
  const handleSelectChange = event => {
    setSessionLength(event.target.value);
  };
  return <>
      {openModalIndex !== null ? <div className='pt-2 px-2 pb-2 flex flex-col items-center justify-center gap-y-4 w-full h-full'>
          <DesktopOpt title={mfaAuthData[openModalIndex].title} authMethodType={mfaAuthData[openModalIndex].authMethodType} pastAuthMethod={mfaAuthData[openModalIndex].status} callBack={ToggleMFA} setOpenModalIndex={setOpenModalIndex} />
        </div> : <div className='flex flex-col lg:flex-row w-full'>
          <div className='w-full'>
            <div className='w-full flex justify-between items-center'>
              <div className='flex flex-col w-2/3 title text-genius-cream text-[20px] font-light'>
                Login Method
              </div>
            </div>
            <div className='py-4 pl-2'>
              {secData.map((infoItem, index) => {
            return <div className='w-full py-4 flex justify-between items-center' key={index}>
                    <div className='flex items-center gap-x-2'>
                      {infoItem.icon}
                      <div className='flex flex-col gap-y-[2px]'>
                        <p className='text-base/4 text-genius-cream'>{infoItem.title}</p>
                        <p className='text-xs/3 text-genius-cream/50'>
                          {`Status: `}
                          {` `}
                          <span className={infoItem.className}>{infoItem.status}</span>
                        </p>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
            <div className='py-4 title text-genius-cream text-[20px] font-light leading-3'>
              <p>Two Factor Authentication</p>
              <p className='mt-4 text-xs leading-4 text-genius-cream/50'>
                Add <span className='text-genius-cream'>one extra method</span> of authenticating
                your account. Choose from the options below
              </p>
            </div>
            <div className='mfa-wrapper'>
              <div className='w-full flex justify-between items-center'>
                <div className='flex flex-col w-full'>
                  <MfAuthDesktop MFAInfo={MFAInfo} mfaAuthData={mfaAuthData} openModalIndex={openModalIndex} setOpenModalIndex={setOpenModalIndex} />
                </div>
              </div>
            </div>
            <div className='session-wrapper'>
              <div className='w-full flex items-center'>
                <div className='flex flex-col title text-genius-cream text-[20px] font-light mt-4'>
                  Session
                </div>
              </div>
              <div className='w-full py-2 flex justify-between items-center'>
                <div className='flex items-center'>
                  <div className='flex flex-col gap-y-[2px]'>
                    <p className=' text-md text-genius-cream'>Session Length</p>
                    <p className=' text-genius-cream/50 text-xs/3'>
                      We will automatically log you out after:
                    </p>
                  </div>
                </div>
                <div className='control'>
                  <div className='control'>
                    <select className='flex items-center justify-between text-genius-cream gap-4 px-2 py-2 rounded-md cursor-pointer text-xs/3 bg-genius-blue text-genius-cream' value={sessionLength} onChange={handleSelectChange}>
                      <option value={SESSION_LENGTHS['1 hour']}>1 Hour</option>
                      <option value={SESSION_LENGTHS['6 hours']}>6 Hours</option>
                      <option value={SESSION_LENGTHS['12 hours']}>12 Hours</option>
                      <option value={SESSION_LENGTHS['1 day']}>1 Day</option>
                      <option value={SESSION_LENGTHS['3 days']}>3 Days</option>
                      <option value={SESSION_LENGTHS['1 week']}>1 Week</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <MobileMenu />
        </div>}
    </>;
}